import { Button, Flex, Pagination } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetDatasetList } from '@/queries/datasetQueries'
import { RouterPath, replacePathParams } from '@/router/paths'
import { DatasetType } from '@/types/dataset'
import { CrudAction, Resource } from '@/types/permissions'
import { getPageCount, getPageOffset } from '@/utils/pagination'
import { buildSortParams } from '@/utils/sorting'
import { FixedPageTitle } from '../layout/PageWithFixedToolbar/FixedPageTitle'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { DataUsageIndicator } from '../subscriptions/DataUsageIndicator/DataUsageIndicator'
import { DataUsageIndicatorWrapper } from '../subscriptions/DataUsageIndicator/DataUsageIndicatorWrapper'
import { useFeatureUsage } from '../subscriptions/useFeatureUsage'
import { useTableState } from '../ui-shared/Tables/useTableState'
import {
  CreateDatasetHandler,
  DatasetCreationResult
} from './CreateDatasetHandler/CreateDatasetHandler'
import { CreateDatasetModal } from './CreateDatasetModal/CreateDatasetModal'
import { DatasetList, TableSortConfig } from './DatasetList/DatasetList'

const PAGE_SIZE = 10

const DEFAULT_SORT_CONFIG: TableSortConfig = {
  key: 'name',
  order: 'asc'
}

export const DatasetListPage = () => {
  const navigate = useNavigate()

  const { usage } = useFeatureUsage('dataset_storage')

  const [
    isCreateModalOpened,
    { open: openCreateModal, close: closeCreateModal }
  ] = useDisclosure(false)

  const [canCreateDataset] = useHasPermissions(Resource.Datasets, [
    CrudAction.Create
  ])

  const [tableState, setTableState] = useTableState({
    activePage: 1,
    sortConfig: DEFAULT_SORT_CONFIG
  })

  const { activePage, sortConfig } = tableState

  const { data, isFetching, isLoading, isError, refetch } = useGetDatasetList(
    {
      is_library: false,
      offset: getPageOffset(activePage, PAGE_SIZE),
      limit: PAGE_SIZE,
      ordering: buildSortParams(sortConfig).ordering
    },
    { keepPreviousData: true }
  )

  const datasets = data?.results || []

  const totalPageCount = getPageCount(data?.count || 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleDatasetCreated = (result: DatasetCreationResult) => {
    const { actionType, dataset } = result

    void navigate(
      replacePathParams(RouterPath.datasetDetails, {
        datasetId: dataset.id
      }),
      {
        state: {
          openCrop:
            actionType === 'create' &&
            dataset.dataset_type === DatasetType.Secondary
        }
      }
    )
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <FixedPageTitle
          title={<FormattedMessage id="datasets.list.title" />}
          leftSection={
            canCreateDataset && (
              <Button
                size="xs"
                miw={140}
                leftSection={<IconPlus size={16} />}
                onClick={openCreateModal}
              >
                <FormattedMessage id="datasets.list.buttons.create" />
              </Button>
            )
          }
        />
      }
    >
      <DatasetList
        datasets={datasets}
        sortConfig={sortConfig}
        isLoading={isLoading || isFetching}
        hasError={isError}
        onReload={() => void refetch()}
        onSort={(sortConfig) => setTableState({ activePage: 1, sortConfig })}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      {usage && !usage.isUnlimited && datasets.length > 0 && (
        <DataUsageIndicatorWrapper maw={360} mt="md" p="md">
          <DataUsageIndicator
            title={<FormattedMessage id="subscriptions.usage.datasetStorage" />}
            tooltip={
              <FormattedMessage id="subscriptions.usage.datasetStorage.description" />
            }
            used={usage.used}
            total={usage.limit}
            unit={usage.unit}
          />
        </DataUsageIndicatorWrapper>
      )}

      <CreateDatasetModal
        opened={isCreateModalOpened}
        onClose={closeCreateModal}
      >
        <CreateDatasetHandler
          onCancel={closeCreateModal}
          onDatasetCreated={handleDatasetCreated}
        />
      </CreateDatasetModal>
    </PageWithFixedToolbar>
  )
}
